import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { APIService, ContentType, ModuleType, PageType, QuestionType } from '../../API.service';
import * as pluralize from 'pluralize'
import { environment } from 'src/environments/environment';
import { ContentGridElement } from '../content-grid/content-grid-element';

@Component({
  selector: 'app-module',
  templateUrl: './module.component.html',
  styleUrls: ['./module.component.scss']
})
export class ModuleComponent implements OnInit {

  ModuleType = ModuleType;
  QuestionType = QuestionType;
  ContentType = ContentType;
  PageType = PageType;
  
  breadcrumb: Array<{
    id: string,
    label: string,
    link: boolean
  }> = []

  moduleContents: Array<ContentDefinition>;
  moduleId: string;
  moduleType: ModuleType;
  userId: string;
  navigationNodeId: string;

  loading: boolean;
  started: boolean;
  finished: boolean;
  selectedIndex: number;

  pages: Array<{
    formGroup: FormGroup
    type: PageType,
    contents: Array<ContentGridElement>,
    loading: boolean,
    completed: boolean
  }> = [];

  constructor(
    private api: APIService, 
    private activatedRoute: ActivatedRoute, 
    private formBuilder: FormBuilder, 
    private router: Router, 
    private sanitizer: DomSanitizer) {}

  ngOnInit(): void {

    this.activatedRoute.params.subscribe(params => {

      this.moduleId = params["id"];

      this.init();

    })

  }

  init() {

    this.loading = true;
    this.started = true;
    this.finished = false;

    this.loadModule();

  }

  loadModule() {

    this.api.getModuleForRun(this.moduleId).then(async module => {

      if (!module.active){
        this.router.navigate(["home"])
        return;
      }

      this.moduleType = module.type;

      this.navigationNodeId = module.navigationNode.id
      this.moduleContents = await Promise.all(module.contents.items.sort((a,b) => {
        return a.order - b.order
      }).map(async (moduleContent) => {
        return await this.toContent(moduleContent.content);
      }))

      this.breadcrumb = [{
        label: module.navigationNode.label,
        id: module.navigationNode.id,
        link: true
      },
      {
        label: module.label,
        id: null,
        link: false
      }];

      var nnc = module.navigationNode.parentConnection;

      while (nnc) {   

        this.breadcrumb.splice(0, 0, {
          id: nnc.parent.id,
          label: nnc.parent.label,
          link: true
        });

        nnc = module.institution.navigationNodeConnections.items.filter((n) => n.id == nnc.parent.id)[0]

      }

      this.breadcrumb.splice(0, 0, {
        id: '',
        label: pluralize(module.institution.navigationNodeLevelNames[0]),
        link: true
      })

      for(let page of module.pages.items.sort((a, b) => a.order - b.order)) {
        
        var contents: Array<ContentGridElement> = [];

        if (page.type == PageType.SLIDE) {
          for (let c of page.slide.contents.items){
            if (c.content != null) {
              var element : ContentGridElement = new ContentGridElement(c.content.type, this.sanitizer, null, c.content.id, c.content.value);
              contents.push(element);
            }
          }
        }

        this.pages.push({
          formGroup: new FormGroup({}),
          loading: false,
          type: page.type,
          contents: contents,
          completed: this.moduleType != ModuleType.SLIDESHOW
        })

      }

    }).then(() => {

      this.loading = false;

    }).catch((e) => {
      console.log(e);
    })

  }

  newChoiceFormGroup = (choiceId: string, choiceValue: string) => {
    return this.formBuilder.group({
      id: this.formBuilder.control(choiceId),
      value: this.formBuilder.control(choiceValue)
    })
  }

  newChoicesResponseFormGroup = () => {
    return this.formBuilder.group({
      choices: this.formBuilder.array([]),
      response: this.formBuilder.group({
        selected: this.formBuilder.control(undefined, Validators.required)
      }),
      answered: this.formBuilder.control(false, Validators.requiredTrue)
    })
  }

  newValueResponseFormGroup = () => {
    return this.formBuilder.group({
      response: this.formBuilder.group({
        value: this.formBuilder.control('', Validators.required),
      }),
      answered: this.formBuilder.control(false, Validators.requiredTrue)
    })
  }

  newLikertResponseFormGroup = (numScales: number) => {

    var scales = Array.from(new Array(numScales)).map(() => this.formBuilder.control(null, [Validators.required]));

    return this.formBuilder.group({
      scales: this.formBuilder.array([]),
      response: this.formBuilder.group({
        scales: this.formBuilder.array(scales)
      }),
      answered: this.formBuilder.control(false, Validators.requiredTrue)
    });

  }

  newLikertScaleFormGroup = (scaleLabel: string, minLabel: string, maxLabel: string, scaleLength: number) => {

    var options = Array.from(new Array(scaleLength)).map((a, i) => {
      return this.formBuilder.control(i)
    })

    return this.formBuilder.group({
      scaleLabel: this.formBuilder.control(scaleLabel),
      minLabel: this.formBuilder.control(minLabel),
      maxLabel: this.formBuilder.control(maxLabel),
      options: this.formBuilder.array(options)
    })

  }

  toContent = async (inputContent) : Promise<ContentDefinition> => {

    var content : {
      src: SafeResourceUrl,
      type: ContentType,
      value: string
    } = {
      src: null,
      type: inputContent.type,
      value: inputContent.value
    }

    var url;

    if (inputContent.type == ContentType.VIDEO){
      url = "https://player.vimeo.com/video/" + inputContent.value
    } else if (inputContent.type == ContentType.IMAGE){
      url = environment.cloudfrontUrl + inputContent.value
    }

    if (url) {
      content.src = this.sanitizer.bypassSecurityTrustResourceUrl(url)
    }

    return content

  }

  async start() {
    this.started = true;
  }

  done() {
    this.router.navigate(["home", this.navigationNodeId])
  }

}

export class ContentDefinition {
  type: ContentType
  value: string
  src: SafeResourceUrl
}